import _ from 'lodash';
// @mui
import { Stack } from '@mui/system';
import { Box, Button, Typography, useTheme } from '@mui/material';
//components
import FormCreator from 'components/FormCreator';
import { fieldType, validationTypes, invalidFields, fieldsGroupType } from 'components/FormCreator/types';
import { FormButton } from 'theme/elements/Buttons';
//reducers
import { selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';
import { getNewBeneficiary, setBeneficiary } from 'state/reducers/beneficiary.reducers';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
//sections
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1 } from 'theme/elements/Headers';
//constants
import { Beneficiary } from 'constants/beneficiary.constants';
//services
import { getPaymentsOptions } from 'services/beneficiary.services';
import { HeaderCaption1, HeaderPage4 } from 'theme/elements/Headers';
import { acceptableFileFormats, maxFileSize } from 'constants/global.constants';
import { CheckCircleOutlineRounded, UploadFile } from '@mui/icons-material';
import ErrorBox from 'components/ErrorBox';
import { balanceTransStepsPrefix } from 'constants/balance.constants';
import { useState } from 'react';
import { getUserDetails, getUserPermission } from 'state/reducers/user.reducers';
import { User, UserPermission } from 'constants/user.constants';
import { validateFileExtensions } from 'utils';
import { uploadPaymentFile } from 'services/pay.services';
import CloseIcon from '@mui/icons-material/Close';

export default function NotesForm(props: {
	handleChange: any;
	invalidFields: invalidFields;
	setInvalidFields: any;
	isCountryDisabled: boolean;
}) {
	const t = useAppSelector(selectTranslations);
	const beneficiary: Beneficiary = useAppSelector(getNewBeneficiary);
	const prefixFields = 'page.beneficiary.create.step.2.';
	const dispatch = useAppDispatch();
	const theme = useTheme();
	const imageUrl = useAppSelector(selectImageUrl);
	const [showErrorBox, setShowErrorBox] = useState(false);
	const [fileUpload, setFileUpload] = useState(false);
	const [errorText, setErrorText] = useState<string | null>(null);
	// existing records have an id - so if there is an id, we are in edit mode
	const isEditMode = Boolean(beneficiary.id);
	const userPermission = useAppSelector(getUserPermission);
	const user: User = useAppSelector(getUserDetails);

	const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		setShowErrorBox(false);

		if (files) {
			const existingFiles = _.get(beneficiary, 'files') || [];
			const totalFilesLength = existingFiles.length + files.length;

			if (totalFilesLength > 6) {
				setErrorText(t(`errors.files.upload.upload_limit_exceeded.title`));
				setShowErrorBox(true);
				event.target.value = '';
				return;
			}

			setFileUpload(true);
			try {
				const uploadPromises = Array.from(files).map(async (file) => {
					if (!validateFileExtensions(file.type)) {
						setErrorText(t(`errors.files.upload.file_type_not_supported.title`));
					}
					if (file.size > maxFileSize) {
						setErrorText(t(`servers.errors.codes.1302`));
					}
					return dispatch(uploadPaymentFile(file, user.companyId));
				});

				const results = await Promise.all(uploadPromises);
				const successfulUploads = results.filter((res) => res?.file);

				if (successfulUploads.length > 0) {
					const existingFiles = _.get(beneficiary, 'files') || [];
					dispatch(
						setBeneficiary({
							name: 'files',
							value: [...existingFiles, ...successfulUploads.map((res) => res.file)],
						})
					);
				} else {
					setErrorText(t(`servers.errors.codes.1303`));
					setShowErrorBox(true);
				}
			} catch (err) {
				setErrorText(err instanceof Error ? err.message : t(`servers.errors.codes.1303`));
				setShowErrorBox(true);
			} finally {
				setFileUpload(false);
			}
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		props.handleChange(event);
	};

	const isNewFile = (file: any, index: number) => {
		if (!isEditMode) return true;
		// Original files have fileId/referenceType, new uploads don't
		return !file.fileId && !file.referenceType;
	};

	const removeFile = (index: number) => {
		const currentFiles = _.get(beneficiary, 'files') || [];
		const fileToRemove = currentFiles[index];

		// In edit mode, only allow deletion of new files
		if (isEditMode && !isNewFile(fileToRemove, index)) {
			return;
		}

		if (_.isArray(currentFiles)) {
			const updatedFiles = currentFiles.filter((file, i) => i !== index);
			dispatch(
				setBeneficiary({
					name: 'files',
					value: updatedFiles,
				})
			);
		}
	};

	return (
		<Box component="form" noValidate>
			<FormCard>
				<FormHeader1 title={t(`${prefixFields}notes.title`)} text={t(`${prefixFields}notes.text`)} />
				<Stack spacing={2}>
					<FormCreator
						initialValidation={props.invalidFields}
						name="notes"
						handleChange={handleChange}
						fieldsGroups={[
							{
								type: fieldsGroupType.col,
								fields: [
									{
										type: fieldType.text,
										props: {
											name: 'comment',
											label: t(`${prefixFields}notes.comment.text`),
											value: beneficiary?.comment || '',
										},
									},
								],
							},
						]}
					/>
				</Stack>
				<Stack spacing={1}>
					<Stack gap={3} direction="column" overflow="hidden">
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{
								backgroundColor: theme.palette.root.brandSecondary_012,
								padding: '32px',
								borderRadius: '8px',
								[theme.breakpoints.down('sm')]: {
									padding: '16px',
								},
							}}
						>
							{!beneficiary.files || beneficiary.files.length === 0 ? (
								<HeaderPage4
									title={t(
										`${balanceTransStepsPrefix}.2.currency_approval.reference_doc.no_file_selected.title`
									)}
									text={t(
										`${balanceTransStepsPrefix}.2.currency_approval.reference_doc.no_file_selected.text`
									)}
								/>
							) : (
								<Stack gap={1} sx={{ maxWidth: '70%' }}>
									{_.isArray(beneficiary.files) &&
										beneficiary.files.map((file, index) => (
											<Stack key={index} flexDirection={'row'} gap={1} alignItems="center">
												{(!isEditMode || isNewFile(file, index)) && (
													<CloseIcon
														onClick={() => removeFile(index)}
														sx={{ height: 'inherit', width: '14px', cursor: 'pointer' }}
													/>
												)}
												<Typography
													sx={{
														overflow: 'hidden',
														whiteSpace: 'nowrap',
														textOverflow: 'ellipsis',
														...(isEditMode &&
															!isNewFile(file, index) && { color: 'text.secondary' }),
													}}
												>
													{decodeURI(
														_.get(file, 'file.originalFileName') ||
															_.get(file, 'originalFileName')
													)}
												</Typography>
											</Stack>
										))}
								</Stack>
							)}
							<FormButton>
								<Button
									variant="outlined"
									sx={{
										'&.MuiButton-outlined': {
											borderColor: theme.palette.root.basicDeepGreen,
										},
									}}
									disabled={fileUpload || (beneficiary?.files && beneficiary.files.length >= 6)}
									component="label"
									startIcon={
										beneficiary?.files && beneficiary.files.length > 0
											? imageUrl(
													'page.balance.add.step2.currency_approval_form.file_upload_button.success.icon'
											  ) || <CheckCircleOutlineRounded />
											: imageUrl(
													'page.balance.add.step2.currency_approval_form.file_upload_button.upload.icon'
											  ) || <UploadFile />
									}
								>
									{t(
										`${balanceTransStepsPrefix}.2.currency_approval.reference_doc.file_upload_button.text`
									)}
									<input
										type="file"
										onChange={handleFileUpload}
										hidden
										accept={acceptableFileFormats}
										multiple={true}
									/>
								</Button>
							</FormButton>
						</Stack>
					</Stack>
					{showErrorBox && (
						<ErrorBox
							icon={
								<img
									src={
										imageUrl('page.balance.add.step2.currency_approval_form.upload.fail.icon') ||
										'/icons/file_error.svg'
									}
								/>
							}
							text={errorText}
						/>
					)}
				</Stack>
			</FormCard>
		</Box>
	);
}
